/**
 * useInvoices Hook
 * React Query hooks for invoice operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import invoiceService from '../services/invoiceService';

// Query keys
export const INVOICE_KEYS = {
  list: (params) => ['invoices', 'list', params],
  download: (invoiceId) => ['invoices', 'download', invoiceId],
  adminConfig: (clientId) => ['invoices', 'admin', 'config', clientId],
  adminPreview: (clientId, month) => ['invoices', 'admin', 'preview', clientId, month],
  adminAll: (params) => ['invoices', 'admin', 'all', params],
};

/**
 * Hook to get list of invoices for current client
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.status - Filter by status
 */
export function useInvoices({ page = 1, limit = 10, status } = {}, queryOptions = {}) {
  return useQuery({
    queryKey: INVOICE_KEYS.list({ page, limit, status }),
    queryFn: () => invoiceService.getInvoices({ page, limit, status }),
    ...queryOptions,
  });
}

/**
 * Hook to get invoice download URL
 * @param {string} invoiceId - Invoice ID
 */
export function useInvoiceDownload(invoiceId) {
  return useQuery({
    queryKey: INVOICE_KEYS.download(invoiceId),
    queryFn: () => invoiceService.getInvoiceDownloadUrl(invoiceId),
    enabled: !!invoiceId,
    staleTime: 5 * 60 * 1000, // URLs valid for 5 minutes
  });
}

/**
 * Hook to download invoice (triggers download)
 */
export function useDownloadInvoice() {
  return useMutation({
    mutationFn: (invoiceId) => invoiceService.downloadInvoice(invoiceId),
  });
}

// =========================================================================
// Admin Hooks
// =========================================================================

/**
 * Hook to get invoice configuration for a client (admin)
 * @param {string} clientId - Client identifier
 */
export function useAdminInvoiceConfig(clientId, queryOptions = {}) {
  return useQuery({
    queryKey: INVOICE_KEYS.adminConfig(clientId),
    queryFn: () => invoiceService.adminGetInvoiceConfig(clientId),
    enabled: !!clientId,
    ...queryOptions,
  });
}

/**
 * Hook to update invoice configuration (admin)
 */
export function useAdminUpdateInvoiceConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, config }) =>
      invoiceService.adminUpdateInvoiceConfig(clientId, config),
    onSuccess: (_, { clientId }) => {
      // Invalidate the config query
      queryClient.invalidateQueries({ queryKey: INVOICE_KEYS.adminConfig(clientId) });
    },
  });
}

/**
 * Hook to preview invoice data (admin)
 * @param {string} clientId - Client identifier
 * @param {string} month - Billing period (YYYY-MM)
 */
export function useAdminInvoicePreview(clientId, month, queryOptions = {}) {
  return useQuery({
    queryKey: INVOICE_KEYS.adminPreview(clientId, month),
    queryFn: () => invoiceService.adminPreviewInvoice(clientId, month),
    enabled: !!clientId && !!month,
    ...queryOptions,
  });
}

/**
 * Hook to generate invoice (admin)
 */
export function useAdminGenerateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, month, sendEmail = true }) =>
      invoiceService.adminGenerateInvoice(clientId, month, sendEmail),
    onSuccess: (_, { clientId }) => {
      // Invalidate invoice lists
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Hook to get all invoices across clients (admin)
 * @param {Object} options - Query options
 */
export function useAdminAllInvoices({ page = 1, limit = 20, status } = {}, queryOptions = {}) {
  return useQuery({
    queryKey: INVOICE_KEYS.adminAll({ page, limit, status }),
    queryFn: () => invoiceService.adminGetAllInvoices({ page, limit, status }),
    ...queryOptions,
  });
}

export default {
  useInvoices,
  useInvoiceDownload,
  useDownloadInvoice,
  useAdminInvoiceConfig,
  useAdminUpdateInvoiceConfig,
  useAdminInvoicePreview,
  useAdminGenerateInvoice,
  useAdminAllInvoices,
};
